import { Col, Pagination, Row, Tag } from 'antd';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { ReactComponent as IconArrow } from '../../icons/icon-arrow.svg';
import { RootStoresContext } from '../../stores/RootStore';
import { searchResultsProps } from '../../stores/SearchStore';
import { cleanString, createMarkup } from '../../util/renderHelpers';
import ErrorPage from '../Error/ErrorPage';
import Loading from '../Loading/Loading';

interface SearchProps extends searchResultsProps {
  query: string;
}

const SearchHeader: React.FC = observer(() => {
  const rootStore = useContext(RootStoresContext);
  return rootStore.searchStore.searchResults && rootStore.searchStore.searchResults.results ? (
    <Col xs={{ span: 24 }} className="search__header">
      <span className="search__header-summary">
        Showing {rootStore.searchStore.getShowingFrom}-{rootStore.searchStore.getShowingTo} of{' '}
        {rootStore.searchStore.searchResults.resultFound} results for{' '}
        <span className="search-query">{rootStore.searchStore.searchResults.query}</span>
      </span>
    </Col>
  ) : null;
});

const Result: React.FC<SearchProps> = props => {
  const query = new RegExp(props.query, 'ig');
  const summary = (str: string) => {
    const words = str.match(query);
    if (!words) {
      return str;
    }
    words.map(el => (str = str.split(el).join(`<strong>${el}</strong>`)));
    return str;
  };
  return (
    <div className="search__result">
      <Link to={props.path} title={cleanString(props.name)}>
        <h2 className="search__result-title">{cleanString(props.name)}</h2>
      </Link>
      {props.metadata.phaseName && (
        <Tag
          className={`search__result-tag${
            props.metadata.phaseColor ? ` ${props.metadata.phaseColor}` : ''
          }`}
        >
          {props.metadata.phasePath ? (
            <Link to={props.metadata.phasePath} title={cleanString(props.metadata.phaseName)}>
              {cleanString(props.metadata.phaseName)}
            </Link>
          ) : (
            cleanString(props.metadata.phaseName)
          )}
        </Tag>
      )}

      {props.summary && (
        <p
          className="search__result-summary"
          dangerouslySetInnerHTML={createMarkup(summary(cleanString(props.summary)))}
        />
      )}
      <div className="search__result-more">
        <Link className="search__result-link" to={props.path} title={cleanString(props.name)}>
          Read More
          <IconArrow />
        </Link>
      </div>
    </div>
  );
};

const SearchResults: React.FC = observer(() => {
  const rootStore = useContext(RootStoresContext);
  const query = rootStore.searchStore.searchResults
    ? rootStore.searchStore.searchResults.query
    : '';
  const displayResults =
    rootStore.searchStore.searchResults && rootStore.searchStore.searchResults.results
      ? rootStore.searchStore.searchResults.results.map(result => (
          <Result key={result.id} query={query} {...result} />
        ))
      : '';
  return rootStore.searchStore.searchResults && rootStore.searchStore.searchResults.results ? (
    <Col className="search__results" xs={{ span: 24 }}>
      {displayResults}
    </Col>
  ) : null;
});

const SearchPagination: React.FC = observer(() => {
  const rootStore = useContext(RootStoresContext);
  const location = useLocation();
  const history = useHistory();
  const query = rootStore.searchStore.searchResults
    ? rootStore.searchStore.searchResults.query
    : '';
  const changePage = useCallback(
    (page: any) => {
      history.push(`${location.pathname}?query=${query}${page !== 1 ? `&page=${page}` : ''}`);
    },
    [location.pathname, history, query]
  );

  return rootStore.searchStore.searchResults && rootStore.searchStore.searchResults.results ? (
    <Row type="flex" justify="center" className="search__pagination">
      <Pagination
        defaultCurrent={Number(rootStore.searchStore.searchResults.currentPage)}
        hideOnSinglePage={true}
        pageSize={Number(process.env.REACT_APP_SEARCH_RESULT_PER_PAGE as string)}
        total={Number(rootStore.searchStore.searchResults.resultFound)}
        onChange={changePage}
      />
    </Row>
  ) : null;
});

const SearchNoResults: React.FC = observer(() => {
  const rootStore = useContext(RootStoresContext);
  const query = rootStore.searchStore.searchResults
    ? rootStore.searchStore.searchResults.query
    : '';
  return rootStore.searchStore.searchResults && rootStore.searchStore.searchResults.noResults ? (
    <Col className="search__results" xs={{ span: 24 }}>
      <div className="search__no-results">
        <h1>
          Search for <strong>{query}</strong>
        </h1>
        <div
          dangerouslySetInnerHTML={createMarkup(
            cleanString(rootStore.searchStore.searchResults.noResults)
          )}
        />
      </div>
    </Col>
  ) : null;
});

const SearchContent: React.FC = observer(() => {
  const rootStore = useContext(RootStoresContext);
  const location = useLocation();

  const findQuery = location.search.match(new RegExp('(?:[?&]query=)([^&]+)'));
  const query = findQuery ? findQuery[1] : null;
  const findPage = location.search.match(new RegExp('(?:[?&]page=)([^&]+)'));
  const page = findPage ? findPage[1] : null;

  const fetchSearch = useCallback(() => {
    rootStore.searchStore.makeSearch(location.pathname, query, page);
  }, [rootStore.searchStore, location.pathname, query, page]);

  useEffect(() => {
    fetchSearch();
  }, [fetchSearch]);

  return (
    <>
      {rootStore.searchStore.isPending || rootStore.pageStore.isPending ? (
        <Col className="hin-loading" xs={{ span: 24 }}>
          <Loading />
        </Col>
      ) : (
        <>
          <SearchHeader />
          <SearchResults />
          <SearchPagination />
          <SearchNoResults />
        </>
      )}
      {rootStore.searchStore.error && (
        <ErrorPage
          errorContent={
            rootStore.siteStore.getPageNotFound ||
            `${rootStore.searchStore.error.name} - ${rootStore.searchStore.error.message}`
          }
        />
      )}
    </>
  );
});

export default SearchContent;
